import { Entity } from "./Entity";

export interface DumpYardTemplate extends Entity {
  address: string;
  zip: string;
  name: string;
  latitude: number;
  longitude: number;
  isDumpYard: boolean;
  isStorage: boolean;
  isQuickStop: boolean;
  // isActive toggles a template off without deleting it. Inactive dump yards are
  // skipped by the fleet optimizer. Normalized so a null/absent backend flag = true.
  isActive: boolean;
  city: string;
  pricePerTon: number | null;
  minDumpWeight: number | null;
  serviceTimeOverride: number | null;
  // Daily operating window as minutes after midnight (0..1440). A null/absent value
  // means the backend applies its 06:00–16:00 default. Applies to dump yards AND
  // storages; the fleet optimizer will not route to a facility outside its window.
  operatingHoursStart: number | null;
  operatingHoursEnd: number | null;
  wasteTypes: string[];
}

// 06:00–16:00, the backend's default operating window, as HH:MM for the form inputs.
export const DEFAULT_OPEN_HHMM = "06:00";
export const DEFAULT_CLOSE_HHMM = "16:00";

// minutesToHHMM renders minutes-after-midnight as a 24h HH:MM string, falling back to
// `fallback` (e.g. the default open/close) when the stored value is null.
export const minutesToHHMM = (minutes: number | null, fallback: string): string => {
  if (minutes == null) return fallback;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// hhmmToMinutes parses a 24h HH:MM string to minutes after midnight, or null if blank/invalid.
export const hhmmToMinutes = (value: string): number | null => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

export enum DumpYardTemplateType {
  DUMP_YARD = "dump_yard",
  STORAGE = "storage",
  QUICK_STOP = "quick_stop",
}

// The closed set of waste categories a dump yard may accept, mirroring the
// backend's validWasteTypes. Persisted as a comma-separated waste_types string.
export const WASTE_TYPES = ["trash", "wood", "concrete"] as const;

export const wasteTypeLabels: Record<string, string> = {
  trash: "Trash",
  wood: "Wood",
  concrete: "Concrete",
};
